import React, { useCallback, useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import TextField from '@material-ui/core/TextField'
import InputAdornment from '@material-ui/core/InputAdornment'
import ExpandLess from '@material-ui/icons/ExpandLess'
import ExpandMore from '@material-ui/icons/ExpandMore'
import Clear from '@material-ui/icons/Clear'
import IconButton from '@material-ui/core/IconButton'
import Fuse from 'fuse.js'
import styled from 'styled-components'

const Container = styled.div`
  position: relative;
  width: 300px;
`

const SuggestionContainer = styled.div`
  background-color: #fff;
  position: absolute;
  max-height: 350px;
  width: 100%;
  top: 56px;
  overflow-y: auto;
  z-index: 1200;
  box-shadow: 0px 0px 10px 0px rgba(0, 0, 0, 0.4);
`

const SuggestionItem = styled.div`
  display: flex;
  align-items: center;
  padding: 10px 16px;
  cursor: pointer;
  font-size: 14px;
  color: #333;
  border-bottom: 1px solid #eee;

  &:hover {
    background-color: #a2a2ff;
    color: #fff;
  }

  &:focus {
    background-color: #a2a2ff;
    color: #fff;
    outline: none;
  }
`

const ProjectCode = styled.span`
  font-weight: 600;
  margin-right: 8px;
`

const FIELDS_TO_SEARCH = ['projectCode', 'name']

const ApprovalProjectSelector = ({ projects, selectedProject, onSelectProject }) => {
  const [inputValue, setInputValue] = useState(selectedProject?.name || '')
  const [visible, setVisible] = useState(false)
  const containerRef = useRef(null)

  useEffect(() => {
    setInputValue(selectedProject?.name || '')
  }, [selectedProject])

  const getFilteredProjects = useCallback(() => {
    const searchValue = inputValue.trim()
    if (!searchValue || searchValue === selectedProject?.name) return projects

    const fuse = new Fuse(projects, {
      shouldSort: true,
      threshold: 0.4,
      location: 0,
      distance: 200,
      maxPatternLength: 32,
      minMatchCharLength: 2,
      keys: FIELDS_TO_SEARCH,
    })
    return fuse.search(searchValue)
  }, [inputValue, projects, selectedProject])

  useEffect(() => {
    const handleClickOutside = e => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setVisible(false)
      }
    }
    document.addEventListener('click', handleClickOutside, false)
    return () => document.removeEventListener('click', handleClickOutside, false)
  }, [])

  const handleSelect = project => {
    onSelectProject(project)
    setInputValue(project.name)
    setVisible(false)
  }

  const handleClear = () => {
    setInputValue('')
    onSelectProject(null)
  }

  const handleInputChange = e => {
    setInputValue(e.target.value)
    if (!visible) setVisible(true)
  }

  const toggleVisible = e => {
    e.stopPropagation()
    setVisible(prev => !prev)
  }

  const filteredProjects = getFilteredProjects()

  return (
    <Container ref={containerRef} data-testid="projectSelector.root">
      <TextField
        label="Select Project"
        fullWidth
        value={inputValue}
        onChange={handleInputChange}
        onClick={() => setVisible(true)}
        inputProps={{ autoComplete: 'off', 'data-testid': 'projectSelector.input' }}
        InputProps={{
          startAdornment: inputValue ? (
            <InputAdornment position="start">
              <IconButton size="small" onClick={handleClear} data-testid="projectSelector.clear">
                <Clear style={{ fontSize: 16, color: '#999' }} />
              </IconButton>
            </InputAdornment>
          ) : null,
          endAdornment: (
            <InputAdornment position="end">
              <IconButton size="small" onClick={toggleVisible} data-testid="projectSelector.toggle">
                {visible ? (
                  <ExpandLess style={{ fontSize: 20, color: '#999' }} />
                ) : (
                  <ExpandMore style={{ fontSize: 20, color: '#999' }} />
                )}
              </IconButton>
            </InputAdornment>
          ),
        }}
      />
      {visible && (
        <SuggestionContainer data-testid="projectSelector.suggestions">
          {filteredProjects.map(project => (
            <SuggestionItem
              key={project._id}
              tabIndex={-1}
              data-testid={`projectSelector.suggestion.${project.projectCode}`}
              onClick={() => handleSelect(project)}
            >
              <ProjectCode>{project.projectCode}</ProjectCode>
              {project.name}
            </SuggestionItem>
          ))}
        </SuggestionContainer>
      )}
    </Container>
  )
}

ApprovalProjectSelector.propTypes = {
  projects: PropTypes.arrayOf(PropTypes.object).isRequired,
  selectedProject: PropTypes.object,
  onSelectProject: PropTypes.func.isRequired,
}

export default ApprovalProjectSelector
